import React, { useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useQueryParams, StringParam } from 'use-query-params'
import styled from 'styled-components'
import { STYLES } from '../../static/style'
import { SchoolList } from './SchoolList'
import { Searching } from './Searching'
import { SchoolSearchResultsToolbar } from './SchoolSearchResultToolbar'
import { SearchResultsLoadingMessage } from '../../components/SearchResults/SearchResultsLoadingMessage'
import { searchSchools } from '../../states/schools/actions'
import { LoadMoreButton } from '../Button/LoadMoreButton'
import { useHorizontalIndicator } from '../../components/User/useHorizontalIndicator'
import { LatLng } from '../../types/LatLng.types'
import SchoolSearchFilter from '../SchoolSearchFilter/SchoolSearchFilter'

const StyledSchoolsSearchResults = styled.div`
  height: 100%;
`

const NoResultsMessage = styled.div`
  padding: 2rem 2rem;
  text-align: center;
`

const LoadMoreButtonContainer = styled.div`
  padding: ${STYLES.GUTTER};
`

const SchoolSearchResultList = styled.div`
  ${({ isHorizontal }: { isHorizontal: boolean }) =>
    isHorizontal &&
    `
    display: flex;
    overflow-x: auto;
    height: 100%;
  `}
`

interface ISchoolSearchResultProps {
  coords?: LatLng
  isShowToolbar?: boolean
  isHorizontal?: boolean
  isOpenInNewTab?: boolean
  isShowLevel?: boolean
  isShowSector?: boolean
  isShowGender?: boolean
  isShowSchoolResults?: boolean
  isShowSchoolFees?: boolean
  isShowDistance?: boolean
  showOnlySchools?: Array<string>
  isShowFilter?: boolean
}

const SchoolSearchResult: React.FC<ISchoolSearchResultProps> = ({
  coords,
  isShowToolbar = true,
  isHorizontal = false,
  isOpenInNewTab,
  isShowDistance,
  isShowLevel,
  isShowSector,
  isShowGender,
  isShowSchoolResults,
  isShowSchoolFees,
  isShowFilter = true,
  showOnlySchools,
}) => {
  const searchResultListEl = useRef(null)
  const listEl = useRef(null)
  const [isShowLeftScrollMoreIndicator, isShowRightScrollMoreIndicator] =
    useHorizontalIndicator(isHorizontal, searchResultListEl, listEl)
  const schools = useSelector((state) => state.schools)
  const dispatch = useDispatch()
  const [query] = useQueryParams({
    search: StringParam,
  })

  if (schools.isSSR) {
    return null
  }
  const { isFetching, currentPage, list: schoolList, totalPages } = schools
  if (isFetching && schoolList.length === 0) {
    return (
      <StyledSchoolsSearchResults>
        <Searching />
      </StyledSchoolsSearchResults>
    )
  }
  if (schoolList.length === 0) {
    return (
      <StyledSchoolsSearchResults>
        {isShowFilter && <SchoolSearchFilter />}
        <NoResultsMessage>Sorry, no results found</NoResultsMessage>
      </StyledSchoolsSearchResults>
    )
  }
  const hasMore = !showOnlySchools && currentPage < totalPages
  let showSchoolList = schoolList
  if (showOnlySchools) {
    showSchoolList = schoolList.filter(
      (school) => !!showOnlySchools.find((sId) => sId === school.id)
    )
  }
  return (
    <StyledSchoolsSearchResults>
      {isShowToolbar && <SchoolSearchResultsToolbar />}
      {isShowFilter && <SchoolSearchFilter />}
      <SchoolSearchResultList
        isHorizontal={isHorizontal}
        ref={searchResultListEl}
      >
        <SchoolList
          listRef={listEl}
          coords={coords}
          isHorizontal={isHorizontal}
          isShowLeftScrollMoreIndicator={isShowLeftScrollMoreIndicator}
          isShowRightScrollMoreIndicator={isShowRightScrollMoreIndicator}
          isOpenInNewTab={isOpenInNewTab}
          schools={showSchoolList}
          isShowDistance={isShowDistance}
          isShowLevel={isShowLevel}
          isShowSector={isShowSector}
          isShowGender={isShowGender}
          isShowSchoolResults={isShowSchoolResults}
          isShowSchoolFees={isShowSchoolFees}
        />
        {!isFetching && hasMore && (
          <LoadMoreButtonContainer>
            <LoadMoreButton
              isHorizontal={isHorizontal}
              onClick={() =>
                dispatch(
                  searchSchools({
                    search: query.search,
                    page: currentPage + 1,
                  })
                )
              }
            />
          </LoadMoreButtonContainer>
        )}
        {isFetching && <SearchResultsLoadingMessage />}
      </SchoolSearchResultList>
    </StyledSchoolsSearchResults>
  )
}

export { SchoolSearchResult }
